import type { FoodPicture } from '../../data/food-pictures';
import { PRODUCTS } from '../../data/products';
import type { IsoDate } from '../../domain/dates';
import { toCents, toEuros } from '../../domain/money';
import { createCatalog, productOf } from '../../domain/price-book';
import type { ShoppingItem } from '../../domain/shopping';
import type { CategoryId, Product, StoreId } from '../../domain/types';
import { compareText, formatQuantity, productName } from '../i18n/format';
import type { Language } from '../i18n/messages';
import { productPicture } from '../pictures/pictures';
import type { AppData, StoredTrip } from '../state/app-data';
import { currentPlan, nextPlan } from '../state/plan-service';

// DE: Reihenfolge wie beim Gang durch den Markt.
// EN: Order as when walking through the store.
/**
 * DE: Kategorien in der Reihenfolge des Weges durch den Laden (Gemüse zuerst).
 * EN: Categories in the order of the walk through the shop (vegetables first).
 */
export const CATEGORY_ORDER: readonly CategoryId[] = [
  'veg',
  'meat',
  'dairy',
  'grain',
  'canned',
  'frozen',
  'basics',
  'drinks',
];

export interface ItemView {
  key: string;
  name: string;
  picture: FoodPicture;
  /**
   * DE: Deutscher Regalname, nur in der ukrainischen Ansicht.
   * EN: German shelf name, only in the Ukrainian view.
   */
  shelfName?: string;
  amount: string;
  total: number;
  estimated: boolean;
  onOffer: boolean;
  checked: boolean;
}

export interface TripView {
  id: string;
  date: IsoDate;
  bought: boolean;
  total: number;
  estimated: boolean;
  savings: number;
  groups: { storeId: StoreId; categories: { category: CategoryId; items: ItemView[] }[] }[];
}

interface ViewContext {
  catalog: ReadonlyMap<string, Product>;
  language: Language;
  storeIds: readonly StoreId[];
}

function amountText(entry: ShoppingItem, product: Product, language: Language): string {
  if (product.packSize === 1) return formatQuantity(entry.packages, product.unit, language);
  return `${entry.packages} × ${formatQuantity(product.packSize, product.unit, language)}`;
}

function itemView(entry: ShoppingItem, trip: StoredTrip, context: ViewContext): ItemView {
  const product = productOf(context.catalog, entry.productId);
  const key = `${entry.productId}|${entry.storeId}`;
  const view = {
    key,
    name: productName(product, context.language),
    picture: productPicture(product),
    amount: amountText(entry, product, context.language),
    total: entry.total,
    estimated: entry.estimated,
    onOffer: entry.onOffer,
    checked: trip.checked.includes(key),
  };
  return context.language === 'uk' ? { ...view, shelfName: product.nameDe } : view;
}

function storeGroup(trip: StoredTrip, storeId: StoreId, context: ViewContext) {
  const items = trip.items.filter((entry) => entry.storeId === storeId);
  const categories = CATEGORY_ORDER.map((category) => ({
    category,
    items: items
      .filter((entry) => productOf(context.catalog, entry.productId).category === category)
      .map((entry) => itemView(entry, trip, context))
      .sort((a, b) => compareText(a.name, b.name, context.language)),
  }));
  return { storeId, categories: categories.filter((entry) => entry.items.length > 0) };
}

function savingsOf(items: readonly ShoppingItem[]): number {
  const cents = items
    .filter((entry) => entry.onOffer)
    .reduce(
      (sum, entry) =>
        sum + (toCents(entry.regularPrice) - toCents(entry.packagePrice)) * entry.packages,
      0,
    );
  return toEuros(cents);
}

function tripView(trip: StoredTrip, context: ViewContext): TripView {
  // DE: Märkte, die nicht mehr gewählt sind, kommen nach den gewählten.
  // EN: Stores that are no longer chosen come after the chosen ones.
  const present = [...new Set(trip.items.map((entry) => entry.storeId))];
  const order = [...context.storeIds, ...present.filter((id) => !context.storeIds.includes(id))];
  return {
    id: trip.id,
    date: trip.date,
    bought: trip.bought,
    total: trip.total,
    estimated: trip.items.some((entry) => entry.estimated),
    savings: savingsOf(trip.items),
    groups: order
      .filter((storeId) => present.includes(storeId))
      .map((storeId) => storeGroup(trip, storeId, context)),
  };
}

/**
 * DE: Einkaufslisten des laufenden und des nächsten Plans: offene nach Datum, erledigte neueste zuerst.
 * EN: Shopping lists of the running and the next plan: open ones by date, done ones latest first.
 */
export function shoppingView(
  data: AppData,
  today: IsoDate,
  language: Language,
): { upcoming: TripView[]; done: TripView[] } {
  const context = { catalog: createCatalog(PRODUCTS), language, storeIds: data.household.storeIds };
  const trips = [currentPlan(data, today), nextPlan(data, today)]
    .flatMap((stored) => stored?.trips ?? [])
    .filter((trip) => trip.items.length > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  const isDone = (trip: StoredTrip) => trip.bought || trip.date < today;
  return {
    upcoming: trips.filter((trip) => !isDone(trip)).map((trip) => tripView(trip, context)),
    done: trips
      .filter(isDone)
      .reverse()
      .map((trip) => tripView(trip, context)),
  };
}
